import { useEffect, useState, type FormEvent } from "react";
import type { Moneda, ObjetivosMensuales } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select } from "../../components/Formulario";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

// Una fila por tenant (no historizada por mes) — ver
// migrations/0009_dashboard_ejecutivo.sql. El Dashboard compara esto contra
// lo acumulado del mes calendario actual.
export function PanelObjetivosConfiguracion({ puedeEditar }: { puedeEditar: boolean }) {
  const { mostrar } = useToast();
  const [valores, setValores] = useState({
    honorarios_cobrados_objetivo: "",
    moneda_objetivo: "ARS" as Moneda,
    escritos_objetivo: "",
    audiencias_objetivo: "",
    nuevos_clientes_objetivo: "",
  });
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api
      .get<{ objetivos: ObjetivosMensuales | null }>("/configuracion/objetivos")
      .then((d) => {
        if (d.objetivos) {
          setValores({
            honorarios_cobrados_objetivo: d.objetivos.honorarios_cobrados_objetivo?.toString() ?? "",
            moneda_objetivo: d.objetivos.moneda_objetivo,
            escritos_objetivo: d.objetivos.escritos_objetivo?.toString() ?? "",
            audiencias_objetivo: d.objetivos.audiencias_objetivo?.toString() ?? "",
            nuevos_clientes_objetivo: d.objetivos.nuevos_clientes_objetivo?.toString() ?? "",
          });
        }
      })
      .catch(() => mostrar("No se pudieron cargar los objetivos.", "error"))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.put("/configuracion/objetivos", {
        honorarios_cobrados_objetivo: valores.honorarios_cobrados_objetivo
          ? Number(valores.honorarios_cobrados_objetivo)
          : null,
        moneda_objetivo: valores.moneda_objetivo,
        escritos_objetivo: valores.escritos_objetivo ? Number(valores.escritos_objetivo) : null,
        audiencias_objetivo: valores.audiencias_objetivo ? Number(valores.audiencias_objetivo) : null,
        nuevos_clientes_objetivo: valores.nuevos_clientes_objetivo
          ? Number(valores.nuevos_clientes_objetivo)
          : null,
      });
      mostrar("Objetivos guardados.", "exito");
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron guardar los objetivos.", "error");
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return null;

  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-texto">Objetivos mensuales</h3>
      <p className="mb-3 text-xs text-texto-secundario">
        Se comparan contra lo acumulado del mes calendario actual en el Dashboard. Dejar en blanco
        el objetivo que no se quiera medir.
      </p>
      <form onSubmit={guardar} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Campo etiqueta="Honorarios cobrados (objetivo)">
          <div className="flex gap-2">
            <Input
              type="number"
              min="0"
              disabled={!puedeEditar}
              value={valores.honorarios_cobrados_objetivo}
              onChange={(e) => setValores((v) => ({ ...v, honorarios_cobrados_objetivo: e.target.value }))}
            />
            <Select
              disabled={!puedeEditar}
              className="w-24"
              value={valores.moneda_objetivo}
              onChange={(e) => setValores((v) => ({ ...v, moneda_objetivo: e.target.value as Moneda }))}
            >
              <option value="ARS">ARS</option>
              <option value="USD">USD</option>
            </Select>
          </div>
        </Campo>
        <Campo etiqueta="Escritos realizados (objetivo)">
          <Input
            type="number"
            min="0"
            disabled={!puedeEditar}
            value={valores.escritos_objetivo}
            onChange={(e) => setValores((v) => ({ ...v, escritos_objetivo: e.target.value }))}
          />
        </Campo>
        <Campo etiqueta="Audiencias (objetivo)">
          <Input
            type="number"
            min="0"
            disabled={!puedeEditar}
            value={valores.audiencias_objetivo}
            onChange={(e) => setValores((v) => ({ ...v, audiencias_objetivo: e.target.value }))}
          />
        </Campo>
        <Campo etiqueta="Nuevos clientes (objetivo)">
          <Input
            type="number"
            min="0"
            disabled={!puedeEditar}
            value={valores.nuevos_clientes_objetivo}
            onChange={(e) => setValores((v) => ({ ...v, nuevos_clientes_objetivo: e.target.value }))}
          />
        </Campo>
        {puedeEditar && (
          <div className="sm:col-span-2">
            <Boton type="submit" cargando={guardando}>
              Guardar objetivos
            </Boton>
          </div>
        )}
      </form>
    </div>
  );
}
